import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export const LOCATION_SOURCE = {
  Device: 'device',
  Typed: 'typed',
} as const;

export type LocationSource = (typeof LOCATION_SOURCE)[keyof typeof LOCATION_SOURCE];

export const DEVICE_LOCATION_LABEL = 'your location';

export type LocationFilterState = {
  origin: { lat: number; lng: number } | null;
  label: string | null;
  distanceMiles: number | null;
  source: LocationSource | null;
};

const STORAGE_KEY = 'cys.locationFilter.v1';

const emptyState: LocationFilterState = {
  origin: null,
  label: null,
  distanceMiles: null,
  source: null,
};

const loadFromStorage = (): LocationFilterState | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.origin) return null;
    return parsed as LocationFilterState;
  } catch {
    return null;
  }
};

const saveToStorage = (state: LocationFilterState) => {
  if (typeof window === 'undefined') return;
  try {
    if (state.origin) {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } else {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {}
};

const initialState: LocationFilterState = loadFromStorage() ?? emptyState;

const slice = createSlice({
  name: 'locationFilter',
  initialState,
  reducers: {
    setTypedLocation(
      state,
      action: PayloadAction<{ lat: number; lng: number; label: string }>,
    ) {
      state.origin = { lat: action.payload.lat, lng: action.payload.lng };
      state.label = action.payload.label;
      state.source = LOCATION_SOURCE.Typed;
    },
    setDeviceLocation(state, action: PayloadAction<{ lat: number; lng: number }>) {
      state.origin = { lat: action.payload.lat, lng: action.payload.lng };
      state.label = DEVICE_LOCATION_LABEL;
      state.source = LOCATION_SOURCE.Device;
    },
    setDistanceMiles(state, action: PayloadAction<number | null>) {
      state.distanceMiles = action.payload;
    },
    clearLocationFilter() {
      return emptyState;
    },
  },
});

export const {
  setTypedLocation,
  setDeviceLocation,
  setDistanceMiles,
  clearLocationFilter,
} = slice.actions;

export default slice.reducer;

const selectLocationFilter = (state: any): LocationFilterState => state.locationFilter;

export function useLocationFilter() {
  const dispatch = useDispatch();
  const state = useSelector(selectLocationFilter);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    saveToStorage(state);
  }, [state]);

  const setTyped = useCallback(
    (loc: { lat: number; lng: number; label: string }) => {
      dispatch(setTypedLocation(loc));
    },
    [dispatch],
  );

  const setDevice = useCallback(
    (loc: { lat: number; lng: number }) => {
      dispatch(setDeviceLocation(loc));
    },
    [dispatch],
  );

  const setDistance = useCallback(
    (miles: number | null) => {
      dispatch(setDistanceMiles(miles));
    },
    [dispatch],
  );

  const clear = useCallback(() => {
    dispatch(clearLocationFilter());
  }, [dispatch]);

  const isActive = state.origin !== null;
  const originString = useMemo(
    () => (state.origin ? `${state.origin.lat},${state.origin.lng}` : null),
    [state.origin],
  );

  return useMemo(
    () => ({
      origin: state.origin,
      label: state.label,
      distanceMiles: state.distanceMiles,
      source: state.source,
      isActive,
      originString,
      setTyped,
      setDevice,
      setDistance,
      clear,
    }),
    [state, isActive, originString, setTyped, setDevice, setDistance, clear],
  );
}
